import React, { FC, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import {
  CollectionProgressItem,
  DetailedProject as DetailedProjectModel,
  isAnnounced,
  ProjectStatus,
  Reservation,
  UserPlan,
} from 'api-client';
import axios from 'axios';

import { createProjectProvider, createReservationManager } from '../../../di';
import { useErrorCallback, useProfileData } from '../../../hooks';
import { RouteDictionary } from '../../../utils';
import { ChevronIcon, CustomLink, Loader } from '../../common';
import DetailedProjectContextProvider from '../../pages/detailed-project/DetailedProjectContext';
import { DetailedProjectContextType } from '../../pages/detailed-project/DetailedProjectContext/DetailedProjectContext';
import DetailedProjectMobileNavigation from '../../pages/detailed-project/DetailedProjectMobileNavigation';
import {
  ProjectGroupPanel,
  ProjectGroupPanelPlacement,
} from '../../pages/detailed-project/ProjectGroupPanel';
import ProjectInvest from '../../pages/detailed-project/ProjectInvest';
import ProjectMain from '../../pages/detailed-project/ProjectMain';
import ProjectReserve from '../../pages/detailed-project/ProjectReserve';

import './DetailedProject.scoped.scss';

type Props = {
  project: DetailedProjectModel;
};

const DetailedProject: FC<Props> = ({ project }) => {
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [reservationLoading, setReservationLoading] = useState(true);
  const [collection, setCollection] = useState<ReadonlyArray<CollectionProgressItem> | null>(null);
  const [collectionLoading, setCollectionLoading] = useState(true);
  const value: DetailedProjectContextType = {
    project: project,
    reservation,
    reservationLoading,
    setReservation,
    collection,
    collectionLoading,
  };
  const profileData = useProfileData();

  const handleError = useErrorCallback();

  const showReserve = Boolean(
    isAnnounced(project.status) &&
    project.reservationLimit &&
    profileData.userPlan === UserPlan.CONOMICA_PLUS
  );
  const showInvest = project.status === ProjectStatus.FUND_RAISING;

  // Блок группы занимает в этой колонке строку под тем блоком, который там уже стоит, а
  // если колонка пустая - поднимается наверх.
  const groupPanelPlacement: ProjectGroupPanelPlacement = showInvest
    ? 'underInvest'
    : showReserve
      ? 'underReserve'
      : 'top';

  useEffect(() => {
    (async () => {
      try {
        const reservationManager = await createReservationManager();
        const result = await reservationManager.getByProject(project.uuid);
        setReservation(result);
      } catch (error) {
        if (!(axios.isAxiosError(error) && error.response?.status === 404)) {
          if (error instanceof Error) await handleError(error);
        }
      } finally {
        setReservationLoading(false);
      }
    })();
  }, [project.uuid]);

  // Ход взыскания грузится здесь, а не во вкладке: навигация решает, показывать ли вкладку,
  // до её открытия. Ошибка намеренно гасится в пустой список - эти данные дополняют
  // карточку проекта, и из-за их недоступности не должна падать вся страница.
  useEffect(() => {
    (async () => {
      setCollectionLoading(true);
      try {
        const projectProvider = await createProjectProvider();
        const progress = await projectProvider.getCollection(project.uuid);
        setCollection(progress.items ?? []);
      } catch {
        setCollection([]);
      } finally {
        setCollectionLoading(false);
      }
    })();
  }, [project.uuid]);

  return (
    <DetailedProjectContextProvider props={value}>
      <CustomLink className="detailed-project-back" to={RouteDictionary.PRIMARY_MARKET}>
        <ChevronIcon direction="left" title="К списку проектов" color={'var(--black-color)'} />
      </CustomLink>
      {reservationLoading ? (
        <Loader />
      ) : (
        <div className="detailed-project">
          <ProjectMain />
          {showReserve && <ProjectReserve />}
          {showInvest && <ProjectInvest />}
          {project.group && (
            <ProjectGroupPanel group={project.group} placement={groupPanelPlacement} />
          )}
          <div className="detailed-project-outlet">
            <DetailedProjectMobileNavigation />
            <Outlet />
          </div>
        </div>
      )}
    </DetailedProjectContextProvider>
  );
};

export default DetailedProject;
